import InView from "inview";
import { CountUp } from "countup.js/dist/countUp.umd";

// Inject the CSS dynamically
const style = document.createElement("style");
style.textContent = `
  .wp-block-embark-stat__inner mask circle {
    stroke-dasharray: 1000;
    stroke-dashoffset: 1000;
    transform: rotate(-90deg);
    transform-origin: 50% 50%;
    opacity: 0;
    will-change: stroke-dashoffset;
  }

  .wp-block-embark-stat__inner.animating mask circle {
    transition: stroke-dashoffset 1.5s ease, opacity 0.3s ease;
    opacity: 1;
  }
`;
document.head.appendChild(style);

export class Stat {
	isCounting = false;
	hasAnimated = false;

	constructor(el) {
		if (!el) return;

		this.el = el;
		this.number = this.parseNumber();
		this.block = this.el.closest(".wp-block-embark-stat__inner");

		this.initializeCountUp();
		this.prepareCircle();
		this.setupInViewObserver();

		// Trigger immediately in editor context
		if (this.isInEditor()) {
			setTimeout(() => this.triggerAnimations(), 300);
		}
	}

	isInEditor() {
		return (
			document.body.classList.contains("wp-admin") ||
			document.body.classList.contains("block-editor-page")
		);
	}

	parseNumber() {
		const hiddenSpan = this.el
			.closest(".wp-block-embark-stat__value")
			?.querySelector(".hidden");
		return hiddenSpan
			? parseFloat(hiddenSpan.textContent.trim())
			: parseFloat(this.el.dataset.number);
	}

	initializeCountUp() {
		const decimalPlaces = String(this.number).includes(".")
			? String(this.number).split(".")[1].length
			: 0;

		this.countUpOptions = {
			decimalPlaces,
			suffix: this.el.textContent.includes("%") ? "%" : "",
			duration: 1.5,
			startVal: 0,
		};

		try {
			this.countUp = new CountUp(this.el, this.number, this.countUpOptions);
			if (this.countUp.error) throw this.countUp.error;
		} catch (e) {
			console.error("CountUp initialization failed:", e);
			this.countUp = null;
		}
	}

	prepareCircle() {
		if (!this.block) return;

		this.animatedCircle = this.block.querySelector("mask circle");
		if (!this.animatedCircle) return;

		const radius = this.animatedCircle.r?.baseVal?.value || 170;
		this.circumference = 2 * Math.PI * radius;
		this.fillPercent = Math.min(this.number, 100);

		// Set initial empty state (0%)
		this.animatedCircle.style.strokeDasharray = `${this.circumference}`;
		this.animatedCircle.style.strokeDashoffset = `${this.circumference}`;
	}

	setupInViewObserver() {
		if (this.isInEditor()) return;
		let targetEl = this.el;
		if (targetEl.classList.contains("js-counter-gauge")) {
			targetEl = this.el.closest(".em-icon-type-gauge");
		}
		this.inView = InView(
			targetEl,
			(isInView) => {
				if (isInView) this.triggerAnimations();
			},
			{
				offset: 100,
				defer: false,
			},
		);
	}

	triggerAnimations() {
		if (this.hasAnimated) return;
		this.hasAnimated = true;

		// Start count animation
		if (this.countUp) {
			this.countUp.start();
		} else {
			this.el.textContent = this.number + (this.countUpOptions.suffix || "");
		}

		if (this.el.classList.contains("js-counter-gauge")) {
			// Add animating class to block
			this.block.classList.add("animating");
		}

		// Animate circle
		if (this.animatedCircle && !isNaN(this.number)) {
			// Add animating class to block
			this.block.classList.add("animating");

			// Force reflow before animation
			void this.animatedCircle.offsetWidth;

			// Animate to target percentage
			this.animatedCircle.style.strokeDashoffset = `${
				this.circumference * (1 - this.fillPercent / 100)
			}`;
		}
	}

	destroy() {
		if (this.inView) this.inView.destroy();
	}
}

export function initStats() {
	// Clean up previous instances
	if (window.embarkStats) {
		window.embarkStats.forEach((stat) => stat.destroy());
	}

	const statEls = [...document.querySelectorAll(".js-counter")];
	if (!statEls.length) return;

	window.embarkStats = statEls.map((el) => new Stat(el));

	// Gutenberg editor compatibility
	if (typeof wp !== "undefined" && wp.data) {
		wp.data.subscribe(() => {
			setTimeout(initStats, 100);
		});
	}
}

// Initialize with multiple fallbacks
const init = () => {
	if (
		document.readyState === "complete" ||
		document.readyState === "interactive"
	) {
		setTimeout(initStats, 100);
	} else {
		document.addEventListener("DOMContentLoaded", initStats);
	}
};

// Expose for manual control
window.embarkInitStats = initStats;

// Start initialization
init();
